import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import { WorkspaceNotFoundError } from './types'
import type {
  AddWorkspaceMemberRequest,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  Workspace,
  WorkspaceJoinRequest,
  WorkspaceOrRedacted,
  WorkspaceSummary,
} from './types'

/** Pull the `title` off a Problem+JSON error body for a user-facing message. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/**
 * The data-layer seam for both Workspaces screens (UID-48b). Shaped
 * field-for-field against the UID-48a `GET /api/workspaces` + `GET
 * /api/workspaces/:id` contract (types.ts's doc comment) — a real
 * `apiClient`-backed implementation is a drop-in replacement for
 * `mockWorkspacesService` (moved to `./test-fixtures.ts` — code-review
 * UID-106 hygiene finding: test doubles must not be reachable from a
 * runtime module); nothing else in this vertical changes.
 *
 * `getWorkspace` and `removeMember` both return `WorkspaceOrRedacted` —
 * per the 2nd-pass security ruling (UID-48a-app 7e6ff4d), a workspace
 * fetch OR a member removal (self-removal from a private workspace) can
 * resolve to the reduced `WorkspaceRedactedStub` shape instead of the full
 * `Workspace`. Every OTHER mutation here (`createWorkspace`/`addMember`)
 * only makes sense against a workspace the caller already has full access
 * to, so those keep returning the full `Workspace`.
 */
export interface WorkspacesService {
  listWorkspaces(): Promise<readonly WorkspaceSummary[]>
  getWorkspace(id: string): Promise<WorkspaceOrRedacted>
  createWorkspace(request: CreateWorkspaceRequest): Promise<Workspace>
  /** UID-177 — persist a Project rename. Returns the updated full workspace
   * (an owner always has full access, so never the redacted stub). */
  renameWorkspace(id: string, request: UpdateWorkspaceRequest): Promise<Workspace>
  requestAccess(id: string): Promise<WorkspaceJoinRequest>
  addMember(id: string, request: AddWorkspaceMemberRequest): Promise<Workspace>
  removeMember(id: string, userId: string): Promise<WorkspaceOrRedacted>
  /** UID-178 — owner-only soft-delete via `DELETE /workspaces/:id`. Resolves
   * with no value: a 204 has no body, unlike every other mutation here. */
  deleteWorkspace(id: string): Promise<void>
}

async function bffListWorkspaces(): Promise<readonly WorkspaceSummary[]> {
  const { data, error } = await apiClient.GET('/workspaces')
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load projects.'))
  }
  return data.workspaces
}

/**
 * CONTRACT-GENERATION NIT (flagged to software-engineer, not fixed here —
 * SOFT tier, no contract change this ticket): `openapi.yaml`'s `GET
 * /workspaces/:id` / `DELETE .../members/:userId` responses declare a
 * `oneOf [Workspace, WorkspaceRedactedStub]` with `discriminator: {
 * propertyName: access }` but no explicit `mapping`. Per the OpenAPI spec,
 * openapi-typescript then falls back to the SCHEMA NAME as the implicit
 * discriminator value, rewriting `Workspace.access`'s generated TS type to
 * the literal `"Workspace"` — clobbering the real `owner|member|account`
 * enum this schema actually declares (and which the server actually sends).
 * The wire payload is correct; only the generated type is wrong. An
 * explicit `discriminator.mapping` (e.g. mapping `owner`/`member`/`account`
 * to `#/components/schemas/Workspace` and `redacted` to
 * `WorkspaceRedactedStub`) would fix this at the source. Until then, every
 * BFF call returning a `Workspace` is cast back to the real, hand-authored
 * `types.ts` shape below.
 */
async function bffGetWorkspace(id: string): Promise<WorkspaceOrRedacted> {
  const { data, error, response } = await apiClient.GET('/workspaces/{workspaceId}', {
    params: { path: { workspaceId: id } },
  })
  if (response.status === 404) {
    throw new WorkspaceNotFoundError(id)
  }
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this project.'))
  }
  return data as unknown as WorkspaceOrRedacted
}

async function bffCreateWorkspace(request: CreateWorkspaceRequest): Promise<Workspace> {
  const { data, error } = await apiClient.POST('/workspaces', { body: request })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t create this project. Try again.'))
  }
  return data as unknown as Workspace
}

async function bffRenameWorkspace(id: string, request: UpdateWorkspaceRequest): Promise<Workspace> {
  const { data, error } = await apiClient.PATCH('/workspaces/{workspaceId}', {
    params: { path: { workspaceId: id } },
    body: request,
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t rename this project. Try again.'))
  }
  // Same discriminator-mapping cast as bffGetWorkspace/bffAddMember (see the doc
  // comment above bffGetWorkspace): the owner-gated PATCH always returns the full
  // Workspace, but the generated type is clobbered to the literal "Workspace".
  return data as unknown as Workspace
}

async function bffRequestAccess(id: string): Promise<WorkspaceJoinRequest> {
  const { data, error } = await apiClient.POST('/workspaces/{workspaceId}/request-access', {
    params: { path: { workspaceId: id } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t request access. Try again.'))
  }
  return data
}

async function bffAddMember(id: string, request: AddWorkspaceMemberRequest): Promise<Workspace> {
  const { data, error } = await apiClient.POST('/workspaces/{workspaceId}/members', {
    params: { path: { workspaceId: id } },
    body: request,
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t add this member. Try again.'))
  }
  return data as unknown as Workspace
}

async function bffRemoveMember(id: string, userId: string): Promise<WorkspaceOrRedacted> {
  const { data, error } = await apiClient.DELETE('/workspaces/{workspaceId}/members/{userId}', {
    params: { path: { workspaceId: id, userId } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t remove this member. Try again.'))
  }
  return data as unknown as WorkspaceOrRedacted
}

/** UID-178: owner-only soft-delete. A 204 has NO body, so — unlike every
 * other mutation in this file — this must NOT guard on `!data`; `data` is
 * always `undefined` on success. Guard on `error` only. */
async function bffDeleteWorkspace(id: string): Promise<void> {
  const { error } = await apiClient.DELETE('/workspaces/{workspaceId}', {
    params: { path: { workspaceId: id } },
  })
  if (error) {
    throw new Error(problemTitle(error, 'Unable to delete project'))
  }
}

/** REAL BFF-backed workspaces service (UID-106) — a signature-compatible
 * drop-in for `mockWorkspacesService` (see this file's doc comment): every
 * method maps 1:1 onto the frozen UID-48a contract. */
export const defaultWorkspacesService: WorkspacesService = {
  listWorkspaces: bffListWorkspaces,
  getWorkspace: bffGetWorkspace,
  createWorkspace: bffCreateWorkspace,
  renameWorkspace: bffRenameWorkspace,
  requestAccess: bffRequestAccess,
  addMember: bffAddMember,
  removeMember: bffRemoveMember,
  deleteWorkspace: bffDeleteWorkspace,
}

export type WorkspaceListState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly workspaces: readonly WorkspaceSummary[] }

export function useWorkspaceList(service: WorkspacesService = defaultWorkspacesService) {
  const [state, setState] = useState<WorkspaceListState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  const serviceRef = useRef(service)
  serviceRef.current = service

  // `generation` is a manual re-fetch trigger, not read in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })
    serviceRef.current
      .listWorkspaces()
      .then((workspaces) => {
        if (!cancelled) setState({ status: 'ready', workspaces })
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'We couldn’t load projects.',
          })
        }
      })
    return () => {
      cancelled = true
    }
  }, [generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  return { state, refetch }
}

export type WorkspaceDetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly workspace: WorkspaceOrRedacted }

export function useWorkspace(id: string, service: WorkspacesService = defaultWorkspacesService) {
  const [state, setState] = useState<WorkspaceDetailState>({ status: 'loading' })
  const [generation, setGeneration] = useState(0)
  const serviceRef = useRef(service)
  serviceRef.current = service

  // `generation` is a manual re-fetch trigger, not read in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })
    serviceRef.current
      .getWorkspace(id)
      .then((workspace) => {
        if (!cancelled) setState({ status: 'ready', workspace })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof WorkspaceNotFoundError) {
          setState({ status: 'not_found' })
          return
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'We couldn’t load this project.',
        })
      })
    return () => {
      cancelled = true
    }
  }, [id, generation])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  const applyMutation = useCallback(async (mutate: () => Promise<WorkspaceOrRedacted>) => {
    const workspace = await mutate()
    setState({ status: 'ready', workspace })
  }, [])

  return { state, refetch, applyMutation }
}
